import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";

const POLL_INTERVAL_MS = 60_000;
const WARN_SECONDS = 300;

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const dialogStyle: CSSProperties = {
  fontFamily: "system-ui, sans-serif",
  backgroundColor: "#fff",
  color: "#111",
  padding: "1.5rem",
  maxWidth: "28rem",
  width: "calc(100% - 2rem)",
  borderRadius: "6px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
};

const buttonRowStyle: CSSProperties = {
  display: "flex",
  gap: "0.75rem",
  marginTop: "1.25rem",
  justifyContent: "flex-end",
};

function isWithinWarningWindow(sessionExpiresAtTimestamp: number): boolean {
  const secondsUntilExpiry = sessionExpiresAtTimestamp - Date.now() / 1000;

  return secondsUntilExpiry <= WARN_SECONDS && secondsUntilExpiry > 0;
}

export function SessionExpiryMonitor() {
  const { token, loading, logout, extendSession } = useAuth();
  const navigate = useNavigate();
  const [warningOpen, setWarningOpen] = useState(false);
  const [expiredOpen, setExpiredOpen] = useState(false);
  const [extendSubmitting, setExtendSubmitting] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);

  const pollUserStatus = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      const status = await api.getUserStatus(token);

      if (status.is_expired) {
        setWarningOpen(false);
        setExpiredOpen(true);

        return;
      }

      if (
        status.session_expires_at_timestamp !== null
        && isWithinWarningWindow(status.session_expires_at_timestamp)
      ) {
        setWarningOpen(true);
      }
    } catch {
      setWarningOpen(false);
      setExpiredOpen(true);
    }
  }, [token]);

  useEffect(() => {
    if (!token || loading) {
      return;
    }

    pollUserStatus();

    const intervalId = window.setInterval(() => {
      pollUserStatus();
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [token, loading, pollUserStatus]);

  async function handleExtendSession() {
    setExtendSubmitting(true);
    setExtendError(null);

    try {
      await extendSession();
      setWarningOpen(false);
    } catch {
      setExtendError("Could not extend your session. Try signing in again.");
    } finally {
      setExtendSubmitting(false);
    }
  }

  function handleExpiredAcknowledge() {
    setExpiredOpen(false);
    logout();
    navigate("/login", { replace: true });
  }

  if (!token || loading) {
    return null;
  }

  return (
    <>
      {warningOpen && !expiredOpen ? (
        <div style={overlayStyle}>
          <div role="dialog" aria-modal="true" aria-labelledby="session-warning-title" style={dialogStyle}>
            <h2 id="session-warning-title" style={{ marginTop: 0 }}>Session ending soon</h2>
            <p>
              Your session will expire in less than five minutes. Extend your session to keep working,
              or dismiss to continue until it expires.
            </p>
            {extendError !== null ? <p role="alert">Error: {extendError}</p> : null}
            <div style={buttonRowStyle}>
              <button type="button" onClick={() => setWarningOpen(false)} disabled={extendSubmitting}>
                Dismiss
              </button>
              <button type="button" onClick={handleExtendSession} disabled={extendSubmitting}>
                {extendSubmitting ? "Extending…" : "Extend session"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {expiredOpen ? (
        <div style={overlayStyle}>
          <div role="dialog" aria-modal="true" aria-labelledby="session-expired-title" style={dialogStyle}>
            <h2 id="session-expired-title" style={{ marginTop: 0 }}>Session expired</h2>
            <p>Your session has ended. Sign in again to continue.</p>
            <div style={buttonRowStyle}>
              <button type="button" onClick={handleExpiredAcknowledge}>
                Sign in again
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
